import {
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Circle,
  Clipboard,
  Download,
  Ellipsis,
  Eye,
  HousePlus,
  Info,
  Mail,
  Signature,
  SquarePen,
  StoreIcon,
  User,
  Users,
} from "lucide-react";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { useNavigate, useParams } from "react-router-dom";
import { useError } from "../../context/ErrorProvider";
import { useQuery, useMutation } from "react-query";
import { getBorrowerById } from "../../sdk/borrower/borrower";
import downloadFromUrl from "../../utils/download";
import {
  getBorrowerGuaranteedLoans,
  getLoansByBorrowerId,
} from "../../sdk/loans/loans";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import { useState } from "react";
import LoanAmount from "../../components/loan-existing-user/LoanAmount";
import Success from "../../components/loan-existing-user/Success";
import { createLoanApplication } from "../../sdk/applications/applications";
import useAuth from "../../hooks/useAuth";
import AcceptTerms from "../../components/loan-existing-user/AcceptTerms";
import { SkeletonLoansPage } from "../../skeletons/LoansSkeletons";

const Borrower = () => {
  const formatAmount = useFormatAmount();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showError } = useError();
  const { auth } = useAuth();
  const formatDate = useFormattedDate();
  const [loanDetails, setLoanDetails] = useState({
    loanProduct: "",
    loanAmount: "",
    loanDuration: "",
    purpose: "",
  });
  const [openLoanDetails, setOpenLoanDetails] = useState(false);
  const [openAcceptTerms, setOpenAcceptTerms] = useState(false);
  const [openLoanApplicationSuccess, setOpenLoanApplicationSuccess] =
    useState(false);

  const toggleLoanDetails = () => {
    setOpenLoanDetails(true);
  };
  const closeLoanDetails = () => {
    setOpenLoanDetails(false);
  };
  const toggleAcceptTerms = () => {
    setOpenAcceptTerms(true);
  };
  const closeAcceptTerms = () => {
    setOpenAcceptTerms(false);
  };

  const handleCollectLoanDetails = () => {
    closeLoanDetails();
    toggleAcceptTerms();
  };

  const handleCreateLoanApplication = () => {
    applicationMutate();
  };

  const toggleLoanApplicationSuccess = () => {
    setOpenLoanApplicationSuccess(true);
  };
  const closeLoanApplicationSuccess = () => {
    setOpenLoanApplicationSuccess(false);
  };

  const { mutate: applicationMutate, isLoading: loadingApplication } =
    useMutation({
      mutationKey: ["application"],
      mutationFn: () =>
        createLoanApplication(
          id,
          auth?.user?.id,
          loanDetails.loanProduct,
          loanDetails.loanAmount,
          loanDetails.loanDuration,
          loanDetails?.purpose,
        ),
      onSuccess: (data) => {
        closeAcceptTerms();
        toggleLoanApplicationSuccess();
      },
      onError: (errors) => {
        showError(errors?.response?.data?.message || errors.message);
      },
    });

  const { data: borrower, isFetching } = useQuery({
    queryKey: ["get borrower", id],
    queryFn: async () => {
      const response = await getBorrowerById(id);
      return response.data.data;
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const { data: myLoans } = useQuery({
    queryKey: ["get borrower loans", id],
    queryFn: () => getLoansByBorrowerId(id),
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const { data: myGuaranteedLoans } = useQuery({
    queryKey: ["get borrower guaranteed loans", id],
    queryFn: () => getBorrowerGuaranteedLoans(id),
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const hasDocuments =
    borrower?.id_front_image?.length > 0 ||
    borrower?.id_back_image?.length > 0 ||
    borrower?.selfie_image?.length > 0 ||
    borrower?.signatureUrl?.length > 0;

  const tabs = [
    {
      id: "personalDetails",
      name: "Personal Details",
      icon: User,
      content: "",
    },
    {
      id: "nextOfKin",
      name: "Next of Kin",
      icon: Users,
      content: "",
    },
    {
      id: "income",
      name: "Income Details",
      icon: Briefcase,
      content: "",
    },
    {
      id: "businessLocation",
      name: "Business Location",
      icon: StoreIcon,
      content: "",
    },
    {
      id: "residenceLocation",
      name: "Residence Location",
      icon: HousePlus,
      content: "",
    },
  ];

  const [activeTab, setActiveTab] = useState(tabs[0].id);

  const fullName = [
    borrower?.firstname,
    borrower?.middlename,
    borrower?.lastname,
  ]
    .filter(Boolean)
    .join(" ");

  const kin =
    borrower?.nextOfKins && borrower.nextOfKins.length > 0
      ? borrower.nextOfKins[0]
      : null;

  const income =
    borrower?.incomes && borrower.incomes.length > 0
      ? borrower.incomes[0]
      : null;

  const business =
    borrower?.businesses && borrower.businesses.length > 0
      ? borrower.businesses[0]
      : null;

  const residence =
    borrower?.residences && borrower.residences.length > 0
      ? borrower.residences[0]
      : null;

  function DetailItem({ label, value }) {
    return (
      <div className="bg-gray-50 p-3 rounded-lg">
        <p className="text-gray-500 text-sm">{label}</p>
        <p className="text-gray-900 font-medium">{value || "—"}</p>
      </div>
    );
  }

  return (
    <>
      {openAcceptTerms && (
        <AcceptTerms
          closeTermsAndConditions={closeAcceptTerms}
          loading={loadingApplication}
          handleNextStep={handleCreateLoanApplication}
          activeIndex={0}
        />
      )}
      {openLoanDetails && (
        <LoanAmount
          closeLoanAmount={closeLoanDetails}
          handleCollectLoanTypeInformation={handleCollectLoanDetails}
          loanDetails={loanDetails}
          setLoanDetails={setLoanDetails}
          loading={false}
        />
      )}
      {openLoanApplicationSuccess && (
        <Success closeDocumentSuccess={closeLoanApplicationSuccess} />
      )}
      {isFetching ? (
        <SkeletonLoansPage />
      ) : (
        <div className="w-full">
          <div className="flex items-center gap-[5px] my-[10px]">
            <ChevronLeft color="#000000" size={17} />
            <p
              onClick={() => navigate("/dashboard/borrowers")}
              className="text-[#222222] text-[16px] cursor-pointer font-bold"
            >
              Borrowers
            </p>
            <ChevronRight color="#000000" size={17} />
            <p className="text-[#222222] capitalize text-[16px] font-bold">
              {borrower?.firstname} {borrower?.lastname}
            </p>
          </div>
          <div className="h-[145px] bg-[#0055A6] rounded-[10px] w-full py-[20px] pl-[10px] pr-[20px] flex justify-between">
            <div className="flex gap-[20px]">
              <div className="h-[94px] w-[94px] rounded-[20px] overflow-hidden">
                {borrower?.selfie_image ? (
                  <img
                    className="h-full w-full object-cover rounded-[20px]"
                    src={borrower?.selfie_image}
                    alt="borrower"
                  />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="94"
                    height="94"
                    viewBox="0 0 24 24"
                  >
                    <g
                      fill="none"
                      stroke="#ffffff"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                    >
                      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0" />
                      <path d="M9 10a3 3 0 1 0 6 0a3 3 0 1 0-6 0m-2.832 8.849A4 4 0 0 1 10 16h4a4 4 0 0 1 3.834 2.855" />
                    </g>
                  </svg>
                )}
              </div>
              <div className="h-full flex justify-around flex-col">
                <p className="text-white font-light text-[14px]">
                  #{borrower?.public_id}
                </p>
                <div className="flex items-center gap-[10px]">
                  <p className="font-medium capitalize text-[16px] text-white">
                    {borrower?.firstname} {borrower?.lastname}
                  </p>
                  <div className="flex items-center gap-[5px]">
                    <Circle size={17} color="#00A676" fill="#00A676" />
                    <p className="text-[#00A676] text-[14px] font-normal">
                      Active
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <p className="font-medium w-[250px] text-[14px] text-[#F3F3F3A6]">
                    Email
                  </p>
                  <p className="font-medium w-[250px] text-[14px] text-[#F3F3F3A6]">
                    Phone
                  </p>
                  <p className="font-medium w-[250px] text-[14px] text-[#F3F3F3A6]">
                    Registration Date
                  </p>
                </div>
                <div className="flex items-start">
                  <p className="font-medium truncate w-[250px] text-[14px] text-[#F3F3F3]">
                    {borrower?.email ?? "N/A"}
                  </p>
                  <p className="font-medium truncate capitalize w-[250px] text-[14px] text-[#F3F3F3]">
                    {borrower?.mobileno ?? "N/A"}
                  </p>
                  <p className="font-medium truncate w-[250px] text-[14px] text-[#F3F3F3]">
                    {formatDate(borrower?.createdAt) ?? "N/A"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-[20px]">
              <div className="w-[44px] h-[40px] border border-[#F3F3F3] rounded-[5px] flex items-center justify-center">
                <Ellipsis color="white" />
              </div>
              <a href={`mailto:${borrower?.email}`}>
                <button className="h-[40px] bg-[#FFFFFF] px-[20px] rounded-[30px] text-[15px] text-[#000000] font-medium flex items-center justify-center gap-[10px]">
                  <Mail size={22} color="#000000" />
                  Send Email
                </button>
              </a>
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 my-2">
            Borrower Details
          </h2>
          <div className="px-[10px] w-full">
            <div className="w-full flex gap-4 pb-3 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg 
              transition-all whitespace-nowrap
              ${
                activeTab === tab.id
                  ? "bg-[#0055A6] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }
            `}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{tab.name}</span>
                </button>
              ))}
            </div>
            {activeTab === "personalDetails" && (
              <div>
                <h2 className="text-[15px] font-semibold text-gray-800 mb-2">
                  Personal Details
                </h2>
                <div className="bg-white shadow-md rounded-xl p-2 w-full">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Full Name */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Full Name</p>
                      <p className="text-gray-900 font-medium">
                        {fullName || "—"}
                      </p>
                    </div>

                    {/* Identification */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Identification</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.identification || "—"}
                      </p>
                    </div>

                    {/* Identification Type */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">
                        Identification Type
                      </p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.identification_type || "—"}
                      </p>
                    </div>

                    {/* Mobile Number */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Mobile Number</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.mobileno || "—"}
                      </p>
                    </div>

                    {/* Email */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Email</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.email || "—"}
                      </p>
                    </div>

                    {/* Date of Birth */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Date of Birth</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.dob || "—"}
                      </p>
                    </div>

                    {/* Country of Residence */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">
                        Country of Residence
                      </p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.country_of_residence || "—"}
                      </p>
                    </div>

                    {/* Citizenship */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Citizenship</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.citizenship || "—"}
                      </p>
                    </div>

                    {/* Gender */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Gender</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.gender || "—"}
                      </p>
                    </div>

                    {/* Public ID */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Public ID</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.public_id || "—"}
                      </p>
                    </div>

                    {/* Postal Address */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Postal Address</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.postal_address || "—"}
                      </p>
                    </div>

                    {/* Alternative Phone Number */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Alternative Phone</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.altPhoneNumber || "—"}
                      </p>
                    </div>

                    {/* Marital Status */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Marital Status</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.maritalStatus || "—"}
                      </p>
                    </div>
                    {/* Dependants */}
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Dependants</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.dependants || "—"}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Date Added</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.createdAt || "—"}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Date Updated</p>
                      <p className="text-gray-900 font-medium">
                        {borrower?.updatedAt || "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === "nextOfKin" && (
              <div>
                <h2 className="text-[15px] font-semibold text-gray-800 mb-2">
                  Next of Kin
                </h2>
                <div className="bg-white shadow-md rounded-xl p-2 w-full">
                  {!kin ? (
                    <p className="text-gray-500 italic">
                      No next of kin added.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailItem label="Name" value={kin.name} />
                      <DetailItem
                        label="Relationship"
                        value={kin.relationship}
                      />
                      <DetailItem
                        label="Phone Number"
                        value={kin.phoneNumber}
                      />
                      <DetailItem
                        label="Date of Birth"
                        value={kin.dateOfBirth}
                      />
                      <DetailItem label="Location" value={kin.location} />
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === "income" && (
              <div>
                <h2 className="text-[15px] font-semibold text-gray-800 mb-2">
                  Income Details
                </h2>
                <div className="bg-white shadow-md rounded-xl p-2 w-full">
                  {!income ? (
                    <p className="text-gray-500 italic">
                      No income details added.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailItem
                        label="Source of Income"
                        value={income.sourceOfIncome}
                      />
                      <DetailItem
                        label="Years of Operation"
                        value={income.yearsOfOperation}
                      />
                      <DetailItem
                        label="Type of Business"
                        value={income.typeOfBusiness}
                      />
                      <DetailItem
                        label="Business Ownership"
                        value={income.businessOwnership}
                      />
                      <DetailItem
                        label="Level of Stability"
                        value={income.levelOfStability}
                      />
                      <DetailItem
                        label="Risks Associated"
                        value={income.risksAssociated}
                      />
                      <DetailItem
                        label="Strength of Business"
                        value={income.strengthOfBusiness}
                      />
                      <DetailItem
                        label="MPESA Statement"
                        value={
                          income.mpesaStatementUrl ? (
                            <a
                              href={income.mpesaStatementUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline"
                            >
                              View Statement
                            </a>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <DetailItem label="MPESA Code" value={income.mpesaCode} />
                      <DetailItem
                        label="Bank Statement"
                        value={
                          income.bankStatementUrl ? (
                            <a
                              href={income.bankStatementUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline"
                            >
                              View Statement
                            </a>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <DetailItem
                        label="Bank Statement Code"
                        value={income.bankStatementCode}
                      />
                      <DetailItem label="Bank Name" value={income.bankName} />
                      <DetailItem
                        label="Business License"
                        value={income.businessLicense}
                      />
                      <DetailItem
                        label="Employer Name"
                        value={income.employerName}
                      />
                      <DetailItem
                        label="Monthly Income"
                        value={income.monthlyIncome || "—"}
                      />
                      <DetailItem
                        label="Duration at Employer"
                        value={income.durationAtEmployer}
                      />
                      <DetailItem
                        label="Business KRA PIN"
                        value={income.businessKraPin}
                      />
                      <DetailItem
                        label="Business Name"
                        value={income.businessName}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === "businessLocation" && (
              <div>
                <h2 className="text-[15px] font-semibold text-gray-800 mb-2">
                  Business Location
                </h2>
                <div className="bg-white shadow-md rounded-xl p-2 w-full">
                  {!business ? (
                    <p className="text-gray-500 italic">
                      No business location added.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailItem
                        label="Business Name"
                        value={business.businessName}
                      />
                      <DetailItem
                        label="County"
                        value={business.businessCounty}
                      />
                      <DetailItem
                        label="Sub-County"
                        value={business.businessSubCounty}
                      />
                      <DetailItem label="Ward" value={business.businessWard} />
                      <DetailItem
                        label="Village"
                        value={business.businessVillage}
                      />
                      <DetailItem
                        label="Latitude"
                        value={business.businessLatitude}
                      />
                      <DetailItem
                        label="Longitude"
                        value={business.businessLongitude}
                      />
                      <DetailItem
                        label="Physical Address"
                        value={business.businessPhysicalAddress}
                      />
                      <DetailItem
                        label="Location Description"
                        value={business.businessLocationDescription}
                      />
                      <DetailItem
                        label="Business Image"
                        value={
                          business.businessImageUrl ? (
                            <a
                              href={business.businessImageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline"
                            >
                              View Image
                            </a>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <DetailItem
                        label="Postal Address"
                        value={business.businessPostalAddress}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === "residenceLocation" && (
              <div>
                <h2 className="text-[15px] font-semibold text-gray-800 mb-2">
                  Residence Location
                </h2>
                <div className="bg-white shadow-md rounded-xl p-2 w-full">
                  {!residence ? (
                    <p className="text-gray-500 italic">
                      No residence location added.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <DetailItem
                        label="County"
                        value={residence.residenceCounty}
                      />
                      <DetailItem
                        label="Sub-County"
                        value={residence.residenceSubCounty}
                      />
                      <DetailItem
                        label="Ward"
                        value={residence.residenceWard}
                      />
                      <DetailItem
                        label="Village"
                        value={residence.residenceVillage}
                      />
                      <DetailItem
                        label="Latitude"
                        value={residence.residenceLatitude}
                      />
                      <DetailItem
                        label="Longitude"
                        value={residence.residenceLongitude}
                      />
                      <DetailItem
                        label="Physical Address"
                        value={residence.residencePhysicalAddress}
                      />
                      <DetailItem
                        label="Location Description"
                        value={residence.residenceLocationDescription}
                      />
                      <DetailItem
                        label="Residence Image"
                        value={
                          residence.residenceImageUrl ? (
                            <a
                              href={residence.residenceImageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline"
                            >
                              View Image
                            </a>
                          ) : (
                            "—"
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="w-full pl-[10px]">
            <div className="w-full">
              <div className="flex my-[20px] pr-[10px] justify-between">
                <div className="h-[400px] p-[20px] w-[49%] bg-[#ffffff] rounded-[10px] shadow-md">
                  <div className="flex items-center justify-between">
                    <p className="text-[#000000] font-semibold text-[20px]">
                      Active Account Financial Summary
                    </p>
                  </div>
                  <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                    <p className="text-[#5C5C5C] text-[16px] font-normal">
                      Total Loan Amounts Borrowed
                    </p>
                    <p className="text-[#000000] font-bold text-[16px]">
                      {formatAmount(
                        borrower?.financialSummary?.totalLoanAmountBorrowed ??
                          0,
                      )}
                    </p>
                  </div>
                  <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                    <p className="text-[#5C5C5C] text-[16px] font-normal">
                      Outstanding Balance
                    </p>
                    <p className="text-[#000000] font-bold text-[16px]">
                      {formatAmount(
                        borrower?.financialSummary?.outstandingBalance ?? 0,
                      )}
                    </p>
                  </div>
                  <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                    <p className="text-[#5C5C5C] text-[16px] font-normal">
                      Next Repayment Due Date
                    </p>
                    <p className="text-[#000000] font-bold text-[16px]">
                      {borrower?.financialSummary?.nextRepaymentDueDate ??
                        "N/A"}
                    </p>
                  </div>
                  <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                    <p className="text-[#5C5C5C] text-[16px] font-normal">
                      Total Repaid
                    </p>
                    <p className="text-[#000000] font-bold text-[16px]">
                      {formatAmount(
                        borrower?.financialSummary?.totalRepaid ?? 0,
                      )}
                    </p>
                  </div>
                  <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                    <p className="text-[#5C5C5C] text-[16px] font-normal">
                      Penalties Issued
                    </p>
                    <p className="text-[#000000] font-bold text-[16px]">
                      {formatAmount(
                        borrower?.financialSummary?.totalPenalties ?? 0,
                      )}
                    </p>
                  </div>
                </div>
                <div className="h-[400px] p-[20px] w-[49%] bg-[#ffffff] rounded-[10px] shadow-md">
                  <p className="text-[#000000] text-[20px] font-semibold">
                    Quick Actions
                  </p>
                  <div className="h-[70px] cursor-pointer w-full my-[25px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]">
                    <div className="w-[10%] flex items-center justify-center h-full">
                      <Signature size={18} color="#000000" />
                    </div>
                    <div
                      onClick={() =>
                        navigate(`/dashboard/borrowers/${id}/edit-borrower`)
                      }
                      className="w-[85%] flex flex-col justify-center h-full cursor-pointer"
                    >
                      <p className="text-[#000000] font-medium text-[14px]">
                        Edit Borrower Info
                      </p>
                      <p className="text-[#737373] text-[12px] font-normal">
                        Update the borrower's personal details or contact info
                      </p>
                    </div>
                    <div className="w-[5%] flex items-center justify-center h-full">
                      <ChevronRight color="#000000" size={24} />
                    </div>
                  </div>
                  <div
                    onClick={toggleLoanDetails}
                    className="h-[70px] cursor-pointer w-full my-[25px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]"
                  >
                    <div className="w-[10%] flex items-center justify-center h-full">
                      <Clipboard size={18} color="#000000" />
                    </div>
                    <div className="w-[85%] flex flex-col justify-center h-full">
                      <p className="text-[#000000] font-medium text-[14px]">
                        Create a New Loan
                      </p>
                      <p className="text-[#737373] text-[12px] font-normal">
                        Start a new loan application for this borrower.
                      </p>
                    </div>
                    <div className="w-[5%] flex items-center justify-center h-full">
                      <ChevronRight color="#000000" size={24} />
                    </div>
                  </div>
                  {/* <div className="h-[70px] cursor-pointer w-full my-[25px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]">
                    <div className="w-[10%] flex items-center justify-center h-full">
                      <SquarePen size={18} color="#000000" />
                    </div>
                    <div className="w-[85%] cursor-pointer flex flex-col justify-center h-full">
                      <p className="text-[#000000] font-medium text-[14px]">
                        Suspend / Reactivate Account
                      </p>
                      <p className="text-[#737373] text-[12px] font-normal">
                        Temporarily restricts this borrower from being assigned
                        new loans.
                      </p>
                    </div>
                    <div className="w-[5%] flex items-center justify-center h-full">
                      <ChevronRight color="#000000" size={24} />
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
            <div className="pr-[10px] w-full">
              <div className="h-[300px] overflow-y-auto p-[10px] mb-[20px] w-full bg-white shadow-md rounded-[10px]">
                <p className="text-[#000000] mb-[10px] text-[18px] font-semibold">
                  Loan History
                </p>
                <div className="h-[44px] px-[10px] w-full bg-[#F3F3F3] mb-[10px] flex items-center">
                  <div className="w-[5%]">
                    <input type="checkbox" name="loan" id="loan" />
                  </div>
                  <div className="w-[14%] text-[#000] font-medium text-[14px]">
                    LOAN ID
                  </div>
                  <div className="w-[15%] text-[#000] truncate font-medium text-[14px]">
                    BORROWER’S NAME
                  </div>
                  <div className="w-[14%] text-[#000] font-medium text-[14px]">
                    LOAN TYPE
                  </div>
                  <div className="w-[14%] text-[#000] font-medium text-[14px]">
                    LOAN AMOUNT
                  </div>
                  <div className="w-[14%] text-[#000] font-medium text-[14px]">
                    BALANCE
                  </div>
                  <div className="w-[14%] text-[#000] font-medium text-[14px]">
                    DUE DATE
                  </div>
                  <div className="w-[14%] text-[#000] font-medium text-[14px]">
                    STATUS
                  </div>
                </div>
                {myLoans?.length > 0 ? (
                  myLoans.map((loan) => (
                    <div className="h-[35px] px-[10px] w-full bg-[#FFFFFF] mb-[10px] flex items-center">
                      <div className="w-[5%]">
                        <input type="checkbox" name="loan" id="loan" />
                      </div>
                      <div
                        onClick={() => navigate(`/dashboard/loans/${loan?.id}`)}
                        className="w-[14%] text-[#000000] cursor-pointer font-medium text-[14px]"
                      >
                        {loan?.loanId}
                      </div>
                      <div className="w-[15%] text-[#000] truncate font-medium text-[14px]">
                        {loan?.borrowerName}
                      </div>
                      <div className="w-[14%] text-[#000] font-medium text-[14px]">
                        {loan?.loanType}
                      </div>
                      <div className="w-[14%] text-[#000] font-medium text-[14px]">
                        {formatAmount(loan?.loanAmount ?? 0)}
                      </div>
                      <div className="w-[14%] text-[#000] font-medium text-[14px]">
                        {formatAmount(loan?.balance ?? 0)}
                      </div>
                      <div className="w-[14%] text-[#000] font-medium text-[14px]">
                        {formatDate(loan?.dueDate)}
                      </div>
                      <div className="w-[14%] capitalize text-[#000] font-medium text-[14px]">
                        {loan?.status}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="w-full flex items-center gap-[10px] justify-center">
                    <Info color="#A8A8A8" size={16} />
                    <p className="text-[#A8A8A8] text-[14px]">
                      Borrower do not have any active loan
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex my-[20px] pr-[10px] justify-between">
              <div className="h-[328px] p-[20px] w-[49%] bg-[#ffffff] rounded-[10px] shadow-md">
                <p className="text-[#000000] mb-[10px] text-[18px] font-semibold">
                  Uploaded Documents
                </p>
                <div className="h-[44px] px-[10px] w-full bg-[#F3F3F3] mb-[10px] flex items-center">
                  <div className="w-[25%] text-[#000] font-medium text-[14px]">
                    DOC TYPE
                  </div>
                  <div className="w-[25%] text-[#000] truncate font-medium text-[14px]">
                    DATE UPLOADED
                  </div>
                  <div className="w-[25%] text-[#000] font-medium text-[14px]">
                    STATUS
                  </div>
                  <div className="w-[25%] text-[#000] font-medium text-[14px]">
                    ACTION
                  </div>
                </div>
                {hasDocuments ? (
                  <>
                    {borrower?.id_front_image?.length > 0 && (
                      <div className="h-[35px] px-[10px] w-full bg-[#FFFFFF] mb-[10px] flex items-center">
                        <div
                          onClick={() =>
                            downloadFromUrl(
                              borrower?.id_front_image,
                              "id_front_image",
                            )
                          }
                          className="w-[25%] cursor-pointer text-[#00A676] font-medium text-[14px]"
                        >
                          ID FRONT IMAGE
                        </div>
                        <div className="w-[25%] text-[#000] truncate font-medium text-[14px]">
                          {formatDate(borrower?.createdAt)}
                        </div>
                        <div className="w-[25%] text-[#000] font-medium text-[14px]">
                          Uploaded
                        </div>
                        <div className="w-[25%] flex items-center gap-[7px] text-[#000] font-medium text-[14px]">
                          <Download size={16} color="grey" />
                          <Eye size={16} color="grey" />
                        </div>
                      </div>
                    )}

                    {borrower?.id_back_image?.length > 0 && (
                      <div className="h-[35px] px-[10px] w-full bg-[#FFFFFF] mb-[10px] flex items-center">
                        <div
                          onClick={() =>
                            downloadFromUrl(
                              borrower?.id_back_image,
                              "id_back_image",
                            )
                          }
                          className="w-[25%] cursor-pointer text-[#00A676] font-medium text-[14px]"
                        >
                          ID BACK IMAGE
                        </div>
                        <div className="w-[25%] text-[#000] truncate font-medium text-[14px]">
                          {formatDate(borrower?.createdAt)}
                        </div>
                        <div className="w-[25%] text-[#000] font-medium text-[14px]">
                          Uploaded
                        </div>
                        <div className="w-[25%] flex items-center gap-[7px] text-[#000] font-medium text-[14px]">
                          <Download size={16} color="grey" />
                          <Eye size={16} color="grey" />
                        </div>
                      </div>
                    )}

                    {borrower?.selfie_image?.length > 0 && (
                      <div className="h-[35px] px-[10px] w-full bg-[#FFFFFF] mb-[10px] flex items-center">
                        <div
                          onClick={() =>
                            downloadFromUrl(
                              borrower?.selfie_image,
                              "selfie_image",
                            )
                          }
                          className="w-[25%] cursor-pointer text-[#00A676] font-medium text-[14px]"
                        >
                          SELFIE IMAGE
                        </div>
                        <div className="w-[25%] text-[#000] truncate font-medium text-[14px]">
                          {formatDate(borrower?.createdAt)}
                        </div>
                        <div className="w-[25%] text-[#000] font-medium text-[14px]">
                          Uploaded
                        </div>
                        <div className="w-[25%] flex items-center gap-[7px] text-[#000] font-medium text-[14px]">
                          <Download size={16} color="grey" />
                          <Eye size={16} color="grey" />
                        </div>
                      </div>
                    )}

                    {borrower?.signatureUrl?.length > 0 && (
                      <div className="h-[35px] px-[10px] w-full bg-[#FFFFFF] mb-[10px] flex items-center">
                        <div
                          onClick={() =>
                            downloadFromUrl(
                              borrower?.signatureUrl,
                              "signatureUrl",
                            )
                          }
                          className="w-[25%] cursor-pointer text-[#00A676] font-medium text-[14px]"
                        >
                          SIGNATURE
                        </div>
                        <div className="w-[25%] text-[#000] truncate font-medium text-[14px]">
                          {formatDate(borrower?.createdAt)}
                        </div>
                        <div className="w-[25%] text-[#000] font-medium text-[14px]">
                          Uploaded
                        </div>
                        <div className="w-[25%] flex items-center gap-[7px] text-[#000] font-medium text-[14px]">
                          <Download size={16} color="grey" />
                          <Eye size={16} color="grey" />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-full w-full text-center text-[#A8A8A8] text-[14px] font-medium mt-[50px]">
                    No documents available.
                  </div>
                )}
              </div>

              <div className="h-[328px] p-[20px] w-[49%] bg-[#ffffff] rounded-[10px] shadow-md">
                <p className="text-[#000000] mb-[10px] text-[18px] font-semibold">
                  Guaranteed Loans
                </p>
                <div className="h-[44px] px-[10px] w-full bg-[#F3F3F3] mb-[10px] flex items-center">
                  <div className="w-[30%] text-[#000] font-medium text-[14px]">
                    LOAN ID
                  </div>
                  <div className="w-[30%] uppercase text-center text-[#000] truncate font-medium text-[14px]">
                    Guaranteed Amount
                  </div>
                  <div className="w-[30%] text-[#000] text-center font-medium text-[14px]">
                    STATUS
                  </div>
                </div>
                <div className="h-[200px] w-full overflow-y-auto">
                  {myGuaranteedLoans?.length > 0 ? (
                    myGuaranteedLoans?.map((loan) => (
                      <div
                        key={loan?.loanId}
                        className="h-[35px] px-[10px] w-full bg-[#FFFFFF] mb-[10px] flex items-center"
                      >
                        <div className="w-[30%] text-[#000] font-medium text-[14px]">
                          {loan?.loanId}
                        </div>
                        <div className="w-[30%] uppercase text-center text-[#000] truncate font-medium text-[14px]">
                          {loan?.loanAmount ?? 0}
                        </div>
                        <div className="w-[30%] text-[#000] text-center font-medium text-[14px]">
                          {loan?.status}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="h-full w-full flex items-center justify-center gap-[10px]">
                      <Info color="#A8A8A8" size={16} />
                      <p className="text-[#A8A8A8] text-[14px]">
                        Borrower has not guaranteed any loan
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Borrower;
